/**
 * "Add to calendar" (M40-T09, spec §C.3.6): an `.ics` file made in the
 * browser, no calendar integration. It holds the pickup time, the hospital,
 * the reference and the transport desk number, and no personal detail beyond
 * the passenger's first name: no surname, phone, address or health detail.
 */
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'

export type CalendarTrip = {
  reference: string
  /** ISO time of the pickup. */
  pickupAt: string
  hospital: string
  firstName: string | null
  /** The transport desk's number, when one is set. */
  desk: string | null
}

/** How long the event lasts in the calendar: a marker, not a promise. */
const EVENT_MINUTES = 60

/** `20311104T093000Z` */
function stamp(moment: Date): string {
  return moment
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
}

/** RFC 5545 text: backslash, semicolon, comma and newlines escaped. */
function text(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

const utf8 = new TextEncoder()

/** Lines longer than 75 octets (UTF-8) are folded with CRLF and a space. The
 * space counts towards the next line's 75, so later pieces carry 74. Whole
 * characters only: a name like "Abẹ́kẹ́" is never cut through the middle. */
function fold(line: string): string {
  const parts: string[] = []
  let piece = ''
  let room = 75
  for (const ch of line) {
    const size = utf8.encode(ch).length
    if (size > room) {
      parts.push(piece)
      piece = ''
      room = 74
    }
    piece += ch
    room -= size
  }
  parts.push(piece)
  return parts.join('\r\n ')
}

export function buildIcs(trip: CalendarTrip, now: Date = new Date()): string {
  const start = new Date(trip.pickupAt)
  const end = new Date(start.getTime() + EVENT_MINUTES * 60_000)
  const who = trip.firstName ? ` for ${trip.firstName}` : ''
  const description = [
    `Trip ${trip.reference} to ${trip.hospital}.`,
    trip.desk ? `Questions: Medcor transport desk ${trip.desk}.` : '',
    `In a medical emergency, call ${EMERGENCY_NUMBER}. Do not wait for this vehicle.`,
  ]
    .filter(Boolean)
    .join(' ')
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Medcor//Transport//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${trip.reference}@transport.medcor`,
    `DTSTAMP:${stamp(now)}`,
    `DTSTART:${stamp(start)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:${text(`Medcor Transport pickup${who}`)}`,
    `LOCATION:${text(trip.hospital)}`,
    `DESCRIPTION:${text(description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return `${lines.map(fold).join('\r\n')}\r\n`
}

/** Hands the file to the browser to save or open. */
export function downloadIcs(trip: CalendarTrip): void {
  const blob = new Blob([buildIcs(trip)], {
    type: 'text/calendar;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${trip.reference}.ics`
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Not at once: some browsers cancel a download whose URL is revoked in the
  // same turn as the click.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
